import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Sparkles, FileText, TrendingUp, Diamond, ArrowRight, AlertCircle } from 'lucide-react';
import Layout from '@/components/Layout';
import StrataMap from '@/components/StrataMap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, synthesisRes, previewRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/synthesis`, { credentials: 'include' }),
        fetch(`${API_URL}/api/synthesis/preview`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (synthesisRes.ok) {
        const synthesisData = await synthesisRes.json();
        setSynthesis(synthesisData);
      }

      if (previewRes.ok) {
        const previewData = await previewRes.json();
        setPreview(previewData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const mapScores = synthesis?.state_scores || preview?.state_scores || {
    ground: 0, observe: 0, connect: 0, execute: 0, integrate: 0
  };

  const sourceProgress = stats ? Math.min((stats.processed_count / 3) * 100, 100) : 0;

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl text-white mb-2">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-400">
              {synthesis 
                ? 'Your Strata Record Synthesis is ready. Continue building your leadership substrate.'
                : 'Build your Strata Record — the substrate of your leadership, layer by layer.'
              }
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/sources">
              <Button variant="outline" className="strata-button-secondary" data-testid="add-source-btn">
                <Upload className="mr-2 h-4 w-4" />
                Add Source
              </Button>
            </Link>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Strata Record Map - Takes 2 columns */}
          <div className="lg:col-span-2">
            <Card className="strata-card-accent h-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-xl text-white">Your Strata Record Map</CardTitle>
                  {synthesis && (
                    <span className="px-3 py-1 rounded-full bg-amber-600/20 text-amber-500 text-xs font-mono">
                      SYNTHESIZED
                    </span>
                  )}
                </div>
                {synthesis?.strata_signature && (
                  <p className="text-slate-400 italic mt-2 text-sm">
                    "{synthesis.strata_signature}"
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex justify-center py-4">
                  <StrataMap scores={mapScores} size={400} animated={true} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="space-y-6">
            {/* Progress Card */}
            <Card className="strata-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-lg text-white">Build Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Sources for Synthesis</span>
                      <span className="text-white font-mono">{stats?.processed_count || 0}/3</span>
                    </div>
                    <Progress value={sourceProgress} className="h-2 bg-slate-mid" />
                  </div>
                  
                  {!stats?.can_synthesize && (
                    <div className="flex items-start gap-2 text-sm text-amber-500">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Add {3 - (stats?.processed_count || 0)} more sources to unlock Strata Record Synthesis</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="strata-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-lg text-white">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/sources" className="block">
                  <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-mid/50" data-testid="action-sources">
                    <FileText className="mr-3 h-5 w-5 text-state-ground" />
                    <span className="flex-1 text-left">Manage Sources</span>
                    <span className="font-mono text-sm text-slate-500">{stats?.sources_count || 0}</span>
                  </Button>
                </Link>
                
                <Link to="/insights" className="block">
                  <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-mid/50" data-testid="action-insights">
                    <Sparkles className="mr-3 h-5 w-5 text-state-connect" />
                    <span className="flex-1 text-left">View Insights</span>
                    {synthesis && <span className="text-amber-500 text-xs">NEW</span>}
                  </Button>
                </Link>
                
                <Link to="/proof" className="block">
                  <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-mid/50" data-testid="action-proof">
                    <Diamond className="mr-3 h-5 w-5 text-state-execute" />
                    <span className="flex-1 text-left">Create Proof</span>
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Feature unlocks */}
            {stats && !stats.purchased_features?.includes('oneview') && stats.can_synthesize && (
              <Card className="strata-card border-amber-600/50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="font-display text-white">Ready for Synthesis</h3>
                      <p className="text-sm text-slate-400">Unlock your Strata Record Synthesis</p>
                    </div>
                  </div>
                  <Link to="/insights">
                    <Button className="w-full strata-button-primary" data-testid="unlock-oneview-btn">
                      Unlock for $29
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Growth Trajectory (if synthesis exists) */}
        {synthesis?.growth_trajectory && (
          <Card className="strata-card">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-500" />
                Growth Trajectory
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Direction</p>
                  <p className="text-white leading-relaxed">{synthesis.growth_trajectory.direction}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">Momentum</p>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm uppercase tracking-wider font-medium ${
                    synthesis.growth_trajectory.momentum === 'accelerating' 
                      ? 'bg-green-500/20 text-green-400'
                      : synthesis.growth_trajectory.momentum === 'steady'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {synthesis.growth_trajectory.momentum}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-2">Key Themes</p>
                  <ul className="space-y-1.5">
                    {synthesis.growth_trajectory.key_themes?.map((theme, idx) => (
                      <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>{theme}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Verified Signals (if synthesis exists) */}
        {synthesis?.verified_signals?.length > 0 && (
          <Card className="strata-card">
            <CardHeader>
              <CardTitle className="font-display text-xl text-white">Verified Signals</CardTitle>
              <p className="text-slate-400 text-sm">Findings confirmed across multiple sources</p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {synthesis.verified_signals.slice(0, 4).map((signal, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg bg-slate-mid/30 border border-slate-mid/50"
                  >
                    <p className="text-white mb-2">{signal.signal}</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`px-2 py-0.5 rounded ${
                        signal.strength === 'high' 
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {signal.strength}
                      </span>
                      <span className="text-slate-500">
                        from {signal.sources?.length || 2} sources
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
